import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Stack, Chip, Button, IconButton,
  CircularProgress, Paper, Divider, Checkbox, Tooltip,
} from '@mui/material';
import {
  AutoAwesome, Close as CloseIcon, CheckCircle, Cancel,
  Refresh, Psychology, MonitorHeart, MedicalServices,
  Healing, Assignment, NoteAlt,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { generateNurseNote } from '../BaseURL';

// System green — matches Compliance / Procurement styling so the FAB,
// dialog header, and CTA all read as part of the same family.
const PRIMARY      = '#41a386';
const PRIMARY_DARK = '#2f8068';
const PRIMARY_BG   = 'rgba(65,163,134,0.10)';

/**
 * Shared dialog for generating AI nurse notes from the patient's loaded
 * clinical context. The data is read from the parent (already loaded by the
 * InPatient view) — we don't refetch here.
 *
 * The nurse picks *which* parameters the AI should use. Anything absent on
 * the patient's chart (no procedure recorded, no vitals on file, etc.) is
 * rendered crossed-out and cannot be toggled on. Anything present is
 * checked by default but can be unticked.
 *
 * Props:
 *  - open, onClose
 *  - patient            { id, fullName, age, gender, visitDate, status }
 *  - complaint, history, exam, other   strings
 *  - diagnoses          [{ name }]
 *  - procedures         [{ name }]
 *  - vitalsHistory      [{ tem, pulse, pre, res, spo2, ... }]
 *  - chartedMedications [{ name, dose, freq, ... }]
 *  - wardRoundsData     [{ ... }]
 *  - onGenerated(text)  populates the parent's nurseNoteText
 */
export default function AINurseNoteDialog({
  open, onClose,
  patient,
  complaint = '', history = '', exam = '', other = '',
  diagnoses = [], procedures = [],
  vitalsHistory = [], chartedMedications = [], wardRoundsData = [],
  onGenerated,
}) {
  const [genIndex, setGenIndex] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState('');
  // Per-parameter selection. Defaults are computed below from what's
  // actually available on the patient's chart.
  const [selected, setSelected] = useState({
    diagnosis: false, procedure: false, plan: false,
    vitals: false, meds: false, rounds: false,
  });

  const latestVitals = vitalsHistory?.[0] || null;
  const diagText  = (diagnoses  || []).map(d => d?.name).filter(Boolean).join(', ');
  const procText  = (procedures || []).map(p => p?.name).filter(Boolean).join(', ');
  const medCount  = (chartedMedications || []).length;
  const roundCount= (wardRoundsData || []).length;

  const planPreview = (other || history || complaint || '').slice(0, 90);

  // What we'll display as the parameter chips — every row shows its presence.
  const params = useMemo(() => ([
    {
      key: 'diagnosis', icon: <Psychology fontSize="small" />, label: 'Diagnosis',
      present: !!diagText,
      preview: diagText || 'Not recorded',
    },
    {
      key: 'procedure', icon: <Healing fontSize="small" />, label: 'Procedure',
      present: !!procText,
      preview: procText || 'None',
    },
    {
      key: 'plan', icon: <Assignment fontSize="small" />, label: 'Doctor\u2019s plan / notes',
      present: !!(other || history || complaint),
      preview: planPreview || 'Not recorded',
    },
    {
      key: 'vitals', icon: <MonitorHeart fontSize="small" />, label: 'Latest vitals',
      present: !!latestVitals,
      preview: latestVitals
        ? [
            latestVitals.tem  ? `T ${latestVitals.tem}\u00B0C` : null,
            latestVitals.pulse? `P ${latestVitals.pulse}`      : null,
            latestVitals.pre  ? `BP ${latestVitals.pre}`       : null,
            latestVitals.res  ? `RR ${latestVitals.res}`       : null,
            latestVitals.spo2 ? `SpO\u2082 ${latestVitals.spo2}%` : null,
          ].filter(Boolean).join(' \u00B7 ')
        : 'Not recorded',
    },
    {
      key: 'meds', icon: <MedicalServices fontSize="small" />, label: 'Active medications',
      present: medCount > 0,
      preview: medCount > 0 ? `${medCount} charted` : 'None',
    },
    {
      key: 'rounds', icon: <NoteAlt fontSize="small" />, label: 'Ward routine / rounds',
      present: roundCount > 0,
      preview: roundCount > 0 ? `${roundCount} round${roundCount === 1 ? '' : 's'} on file` : 'None',
    },
  ]), [diagText, procText, planPreview, latestVitals, medCount, roundCount]);

  // Whenever the dialog opens, reset state and pre-tick every parameter that
  // actually has data on file. The nurse can then untick any they don't want
  // the AI to consider.
  useEffect(() => {
    if (open) {
      setGenIndex(0);
      setGenerated('');
      setGenerating(false);
      const next = {};
      params.forEach(p => { next[p.key] = !!p.present; });
      setSelected(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (key) => {
    setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const someSelected = Object.values(selected).some(Boolean);

  const handleGenerate = async () => {
    if (!patient?.id) {
      Swal.fire('No patient', 'Select a patient before generating a note.', 'info');
      return;
    }
    if (!someSelected) {
      Swal.fire('No parameters selected', 'Tick at least one parameter for the AI to consider.', 'info');
      return;
    }

    // Build a vitals snippet — one short line per recent record so the AI
    // can ground itself in the trend, not just the latest.
    const vitalsText = selected.vitals
      ? (vitalsHistory || []).slice(0, 4).map(v => {
          const bits = [
            v.tem   ? `T ${v.tem}\u00B0C` : null,
            v.pulse ? `P ${v.pulse}` : null,
            v.pre   ? `BP ${v.pre}` : null,
            v.res   ? `RR ${v.res}` : null,
            v.spo2  ? `SpO\u2082 ${v.spo2}%` : null,
            v.we    ? `W ${v.we}kg` : null,
          ].filter(Boolean).join(', ');
          const when = [v.dat_vist || v.dat, v.time].filter(Boolean).join(' ');
          return [when, bits].filter(Boolean).join(' — ');
        }).filter(Boolean).join('\n')
      : '';

    const medsText = selected.meds
      ? (chartedMedications || []).slice(0, 10).map(m => {
          const bits = [m.name, m.dose, m.freq, m.rou].filter(Boolean).join(' ');
          return bits || null;
        }).filter(Boolean).join('\n')
      : '';

    const roundsText = selected.rounds
      ? (wardRoundsData || []).slice(0, 4).map(r => {
          const when = r.dat || r.date || r.created_at || '';
          const txt  = r.note || r.text || r.entry || r.round || '';
          return [when, txt].filter(Boolean).join(' — ');
        }).filter(Boolean).join('\n')
      : '';

    setGenerating(true);
    try {
      const payload = {
        patientId:   patient.id,
        patientName: patient.fullName,
        age:         patient.age,
        gender:      patient.gender,
        actionDate:    patient.visitDate,
        selectedDate:  patient.visitDate,
        startDate:     patient.visitDate,
        endDate:       new Date().toISOString().slice(0, 10),
        // Only include the params the nurse ticked; the rest are sent
        // as empty so the backend prompt skips them naturally.
        diagnosisText:  selected.diagnosis ? diagText : '',
        proceduresText: selected.procedure ? procText : '',
        treatmentPlan:  selected.plan ? (other || history || '') : '',
        complaint:      selected.plan ? complaint : '',
        physicalExam:   selected.plan ? exam      : '',
        otherDoc:       selected.plan ? other     : '',
        vitalsText,
        medicationsText: medsText,
        wardRoundsText:  roundsText,
        // Boolean toggles so the backend can branch even when the text
        // fields above are empty (e.g. nurse picked vitals only).
        includeDiagnosis:     selected.diagnosis,
        includeProcedure:     selected.procedure,
        includePlan:          selected.plan,
        includeVitals:        selected.vitals,
        includeMedications:   selected.meds,
        includeWardRoutine:   selected.rounds,
        isDischarge: false,
        isDetention: patient.status === 'Detention',
        isOPD: false,
        generationIndex: genIndex,
        excludeNamesFromNotes: true,
      };

      const res = await axios.post(generateNurseNote, payload);
      if (res?.data?.success) {
        const txt = res.data?.data?.nurseNote || '';
        setGenerated(txt);
        setGenIndex(i => i + 1);
      } else {
        throw new Error(res.data?.message || 'AI generation failed');
      }
    } catch (err) {
      console.error('AI nurse note error', err);
      const blocked = err.response?.status === 403 && err.response?.data?.licenseBlocked;
      Swal.fire(
        blocked ? 'AI Disabled' : 'Generation Failed',
        blocked ? 'AI generation is disabled on this license.' : (err.response?.data?.message || err.message || 'Could not generate the note.'),
        'error',
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleUse = () => {
    if (generated && typeof onGenerated === 'function') {
      onGenerated(generated);
    }
    onClose && onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, py: 1.75, px: 2,
        bgcolor: PRIMARY, color: 'white',
      }}>
        <AutoAwesome />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.8px', opacity: 0.9 }}>
            NURSING · AI NOTE
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
            Generate Nursing Note
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', opacity: 0.9, mt: 0.25 }}>
            {patient?.fullName ? `${patient.fullName} \u00B7 ID ${patient.id}` : 'Select a patient first'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2, bgcolor: '#f8fafc' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{
            fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
            textTransform: 'uppercase', letterSpacing: '0.6px',
          }}>
            Parameters to consider
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#64748b', fontStyle: 'italic' }}>
            Tick the ones the AI should use.
          </Typography>
        </Box>

        <Stack spacing={0.75}>
          {params.map(p => {
            const isChecked = !!selected[p.key];
            return (
              <Paper
                key={p.key}
                elevation={0}
                onClick={() => p.present && toggle(p.key)}
                sx={{
                  p: 1, borderRadius: 1.5,
                  border: `1px solid ${isChecked ? 'rgba(65,163,134,0.4)' : '#e5e9ef'}`,
                  bgcolor: !p.present ? '#f1f5f9' : (isChecked ? PRIMARY_BG : '#fff'),
                  display: 'flex', alignItems: 'center', gap: 1,
                  cursor: p.present ? 'pointer' : 'not-allowed',
                  opacity: p.present ? 1 : 0.65,
                  transition: 'all 0.15s ease',
                  '&:hover': p.present ? {
                    borderColor: 'rgba(65,163,134,0.5)',
                    bgcolor: isChecked ? PRIMARY_BG : 'rgba(65,163,134,0.05)',
                  } : {},
                }}
              >
                <Tooltip
                  arrow
                  title={p.present
                    ? (isChecked ? 'Click to exclude from the note' : 'Click to include in the note')
                    : 'Not available on this chart'}
                >
                  <span>
                    <Checkbox
                      size="small"
                      checked={isChecked && p.present}
                      disabled={!p.present}
                      onChange={() => toggle(p.key)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        p: 0.5,
                        color: '#cbd5e1',
                        '&.Mui-checked': { color: PRIMARY },
                      }}
                    />
                  </span>
                </Tooltip>

                <Box sx={{
                  width: 26, height: 26, borderRadius: 1,
                  bgcolor: p.present ? (isChecked ? PRIMARY_BG : '#f1f5f9') : '#e2e8f0',
                  color:   p.present ? (isChecked ? PRIMARY_DARK : '#64748b') : '#94a3b8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {p.icon}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '0.78rem', fontWeight: 700,
                    color: p.present ? '#0f172a' : '#94a3b8',
                    textDecoration: p.present ? 'none' : 'line-through',
                    lineHeight: 1.2,
                  }}>
                    {p.label}
                  </Typography>
                  <Typography noWrap sx={{
                    fontSize: '0.72rem',
                    color: p.present ? '#64748b' : '#94a3b8',
                    fontStyle: p.present ? 'normal' : 'italic',
                    mt: 0.25,
                  }}>
                    {p.preview}
                  </Typography>
                </Box>

                {p.present
                  ? <CheckCircle sx={{ color: PRIMARY, fontSize: 18, opacity: isChecked ? 1 : 0.35 }} />
                  : <Cancel sx={{ color: '#cbd5e1', fontSize: 18 }} />}
              </Paper>
            );
          })}
        </Stack>

        {/* Generated note preview */}
        {generated && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ my: 1.5 }}>
              <Chip label="Generated" size="small" sx={{
                bgcolor: PRIMARY_BG, color: PRIMARY_DARK, fontWeight: 700,
              }} />
            </Divider>
            <Paper elevation={0} sx={{
              p: 1.5, borderRadius: 1.5, border: '1px solid #e5e9ef', bgcolor: '#fff',
            }}>
              <Typography sx={{ fontSize: '0.9rem', color: '#0f172a', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {generated}
              </Typography>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, bgcolor: '#fff', borderTop: '1px solid #e5e9ef' }}>
        {!generated ? (
          <>
            <Button onClick={onClose} disabled={generating} sx={{ color: '#64748b', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={generating || !someSelected}
              disableElevation
              startIcon={generating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <AutoAwesome />}
              sx={{
                bgcolor: PRIMARY, color: '#fff',
                textTransform: 'none', fontWeight: 700, px: 3,
                '&:hover': { bgcolor: PRIMARY_DARK },
                '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#fff' },
              }}
            >
              {generating ? 'Generating\u2026' : 'Generate Note'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} sx={{ color: '#64748b', textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={generating}
              startIcon={generating ? <CircularProgress size={16} /> : <Refresh />}
              sx={{ color: PRIMARY_DARK, textTransform: 'none', fontWeight: 700 }}
            >
              Try another
            </Button>
            <Button
              variant="contained"
              onClick={handleUse}
              disableElevation
              startIcon={<CheckCircle />}
              sx={{
                bgcolor: PRIMARY, color: '#fff',
                textTransform: 'none', fontWeight: 700, px: 3,
                '&:hover': { bgcolor: PRIMARY_DARK },
              }}
            >
              Use This Note
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
